// src/plugins/jwtVerifier.js
import { 
    jwtVerifier, 
    initTokenVerification, 
    startPeriodicVerification,
    verifyResponseSign 
} from '@/utils/coreRequest'

const JwtVerifierPlugin = {
    install(app, options = {}) {
        const { 
            autoInit = true, 
            periodicCheck = true, 
            checkInterval = 120 
        } = options
        
        // 创建验证器实例
        const verifier = {
            // 验证器实例
            verifier: jwtVerifier,
            
            // 验签函数
            verifyResponseSign: verifyResponseSign,
            
            // 验证状态
            isVerifying: false,
            isVerified: false,
            verificationResult: null,
            
            // 初始化验证
            async init() {
                try {
                    this.isVerifying = true
                    // console.log('开始JWT验证...')
                    
                    const result = await initTokenVerification()
                    this.isVerified = result.success
                    this.verificationResult = result
                    
                    if (result.success) {
                        // console.log('✅ JWT验证成功')
                        this.emit('verified', result)
                    } else if (result.bypass) {
                        console.warn('⚠️ 接口失败，已放行')
                        this.emit('bypassed', result)
                    } else {
                        console.error('❌ JWT验证失败:', result.error)
                        this.emit('failed', result)
                    }
                    
                    return result
                } catch (error) {
                    console.error('JWT验证异常:', error)
                    this.emit('error', error)
                    throw error
                } finally {
                    this.isVerifying = false
                }
            },
            
            // 手动重新验证
            async revalidate() {
                // console.log('手动重新验证JWT...')
                return await this.init()
            },
            
            // 获取当前令牌
            getCurrentToken() {
                return localStorage.getItem('auth_token')
            },
            
            // 获取令牌信息
            getTokenInfo() {
                const token = this.getCurrentToken()
                if (!token) return null
                
                try {
                    return jwtVerifier.decode(token)
                } catch (error) {
                    console.error('解码令牌失败:', error)
                    return null
                }
            },
            
            // 检查令牌是否过期
            isTokenExpired() {
                const token = this.getCurrentToken()
                if (!token) return true
                
                const check = jwtVerifier.checkExpiration(token)
                return !check.isValid || check.expired
            },
            
            // 获取剩余时间（秒）
            getRemainingTime() {
                const token = this.getCurrentToken()
                if (!token) return 0
                
                const check = jwtVerifier.checkExpiration(token)
                return check.remainingSeconds || 0
            },
            
            // 获取格式化后的剩余时间
            getFormattedRemainingTime() {
                const seconds = this.getRemainingTime()
                if (seconds <= 0) return '已过期'
                
                if (seconds < 60) {
                    return `${Math.floor(seconds)}秒`
                } else if (seconds < 3600) {
                    return `${Math.floor(seconds / 60)}分钟`
                } else if (seconds < 86400) {
                    return `${Math.floor(seconds / 3600)}小时`
                } else {
                    return `${Math.floor(seconds / 86400)}天`
                }
            },
            
            // 清除令牌
            clearToken() {
                localStorage.removeItem('auth_token')
                localStorage.removeItem('auth_token_expiry')
                localStorage.removeItem('jwt_token_cache')
                localStorage.removeItem('jwt_last_verify')
                this.isVerified = false
                this.verificationResult = null
                // console.log('令牌已清除')
                this.emit('cleared')
            },
            
            // 完整验证令牌
            async verifyToken(token) {
                try {
                    return await jwtVerifier.fullVerify(token)
                } catch (error) {
                    console.error('令牌验证失败:', error)
                    return {
                        valid: false,
                        error: error.message
                    }
                }
            },
            
            // 验证响应签名
            async verifySignature(responseData) {
                try {
                    return await verifyResponseSign(responseData)
                } catch (error) {
                    console.error('签名验证失败:', error)
                    return false
                }
            },
            
            // 事件系统
            events: {},
            on(event, callback) {
                if (!this.events[event]) this.events[event] = []
                this.events[event].push(callback)
            },
            off(event, callback) {
                if (!this.events[event]) return
                const index = this.events[event].indexOf(callback)
                if (index > -1) this.events[event].splice(index, 1)
            },
            emit(event, data) {
                if (!this.events[event]) return
                this.events[event].forEach(callback => callback(data))
            }
        }
        
        // 在 Vue 实例上添加全局属性
        app.config.globalProperties.$jwt = verifier
        
        // 自动初始化
        if (autoInit) {
            app.config.globalProperties.$jwt.init()
        }
        
        // 启动定期验证
        if (periodicCheck) {
            let intervalId = null
            app.config.globalProperties.$jwt.startPeriodicCheck = () => {
                if (intervalId) clearInterval(intervalId)
                intervalId = startPeriodicVerification(checkInterval)
                app.config.globalProperties.$jwt.periodicCheckId = intervalId
                // console.log(`已启动定期验证，每${checkInterval}分钟一次`)
            }
            
            app.config.globalProperties.$jwt.stopPeriodicCheck = () => {
                if (intervalId) {
                    clearInterval(intervalId)
                    intervalId = null
                    app.config.globalProperties.$jwt.periodicCheckId = null
                    // console.log('已停止定期验证')
                }
            }
            
            // 默认启动
            app.config.globalProperties.$jwt.startPeriodicCheck()
        }
        
        // 在组件中提供注入
        app.provide('jwt', app.config.globalProperties.$jwt)
    }
}

export default JwtVerifierPlugin